import React, { useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { Close, Schedule } from '@material-ui/icons'
import { ApexOptions } from "apexcharts"
import ReactApexChart from "react-apexcharts"
import { StatCard } from 'components/StatCard'

interface IHourDetailModalProps {
  open: boolean
  onClose: () => void
  hourData: {
    hour: string
    users: any[]
    avg_secs: number
    total_items: number
    total_secs: number
  } | null
}

const HourDetailModal = ({
  open,
  onClose,
  hourData
}: IHourDetailModalProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { separatorData, stats } = useMemo(() => {
    if (!hourData || !hourData.users) {
      return {
        separatorData: [],
        stats: {
          totalSeparators: 0,
          avgTime: 0,
          bestPerformer: 'N/A',
          worstPerformer: 'N/A',
          totalItems: 0,
          productivity: 0
        }
      }
    }

    // Processar dados dos separadores
    const separatorData = hourData.users
      .map(user => ({
        username: user.username,
        avgSeconds: user.avg_secs,
        totalItems: user.total_items,
        totalSecs: user.total_secs,
        formattedName: user.username
          .split('.')
          .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
      }))
      .sort((a, b) => a.avgSeconds - b.avgSeconds) // Ordenar por performance (melhor primeiro)

    // Calcular estatísticas
    const stats = {
      totalSeparators: separatorData.length,
      avgTime: hourData.avg_secs,
      bestPerformer: separatorData[0]?.formattedName || 'N/A',
      worstPerformer: separatorData[separatorData.length - 1]?.formattedName || 'N/A',
      totalItems: hourData.total_items,
      productivity: hourData.total_items / (hourData.total_secs / 3600) // itens por hora
    }

    return { separatorData, stats }
  }, [hourData])

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 320,
      toolbar: { show: false },
      fontFamily: 'Public Sans, sans-serif',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: separatorData.length > 15 ? '90%' : '70%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: separatorData.length <= 12,
      formatter: (val: number) => `${val.toFixed(0)}s`,
      offsetY: -20,
      style: {
        fontSize: '10px',
        fontWeight: 600,
        colors: ['#9C27B0'],
      },
    },
    xaxis: {
      categories: separatorData.map(data => data.formattedName),
      labels: {
        rotate: separatorData.length > 8 ? -45 : 0,
        style: { fontSize: '10px' },
        maxHeight: separatorData.length > 8 ? 80 : 60,
        trim: false,
        hideOverlappingLabels: false,
      },
    },
    yaxis: {
      title: {
        text: 'Tempo Médio (segundos)',
        style: { fontSize: '12px', fontWeight: 600 },
      },
      labels: {
        formatter: (value) => `${value.toFixed(0)}s`
      }
    },
    colors: ['#9C27B0'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    tooltip: {
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const separator = separatorData[dataPointIndex]
        if (!separator) return ''

        const rankPosition = dataPointIndex + 1
        const isTopPerformer = rankPosition <= 3
        const isBottomPerformer = rankPosition > separatorData.length - 3

        return `
          <div style="padding: 12px; min-width: 200px;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #1976d2;">
              ${separator.formattedName}
            </div>

            <div style="margin-bottom: 8px;">
              <strong>Tempo médio:</strong> ${separator.avgSeconds.toFixed(0)}s
            </div>
            <div style="margin-bottom: 8px;">
              <strong>Itens processados:</strong> ${separator.totalItems.toLocaleString()}
            </div>
            <div style="margin-bottom: 8px;">
              <strong>Produtividade:</strong> ${(separator.totalItems / (separator.totalSecs / 3600)).toFixed(1)} itens/h
            </div>

            <div style="padding: 6px; background: ${isTopPerformer ? '#e8f5e8' : isBottomPerformer ? '#ffebee' : '#f5f5f5'}; border-radius: 4px; margin-top: 8px;">
              <div style="font-size: 12px; font-weight: 600; color: ${isTopPerformer ? '#4CAF50' : isBottomPerformer ? '#f44336' : '#666'};">
                ${isTopPerformer ? '🏆 Top Performer' : isBottomPerformer ? '⚠️ Precisa Atenção' : '📊 Performance Média'}
              </div>
              <div style="font-size: 11px; color: #666;">
                Posição: ${rankPosition}º de ${separatorData.length}
              </div>
            </div>
          </div>
        `
      }
    },
    annotations: {
      yaxis: [
        {
          y: stats.avgTime,
          borderColor: '#FF9800',
          strokeDashArray: 5,
          borderWidth: 2,
          label: {
            borderColor: '#FF9800',
            style: {
              color: '#fff',
              background: '#FF9800',
            },
            text: `Média: ${stats.avgTime.toFixed(0)}s`,
          },
        },
      ],
    },
  }

  const series = [
    {
      name: 'Tempo Médio',
      data: separatorData.map(data => data.avgSeconds),
    },
  ]

  if (!hourData) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? false : "lg"}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          minHeight: isMobile ? '100vh' : '60vh',
          maxHeight: isMobile ? '100vh' : '80vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
            <Schedule color="primary" />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Análise Detalhada - {hourData.hour}h
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Cards de Estatísticas */}
        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Separadores Ativos"
              value={stats.totalSeparators.toString()}
              color="#FFFFFF"
              gradientColors={['#1976d2', '#1565c0']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Tempo Médio"
              value={`${stats.avgTime.toFixed(0)}s`}
              color="#FFFFFF"
              gradientColors={['#FF9800', '#f57c00']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Itens Processados"
              value={stats.totalItems.toLocaleString()}
              color="#FFFFFF"
              gradientColors={['#4CAF50', '#388e3c']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Produtividade"
              value={`${stats.productivity.toFixed(0)} itens/h`}
              color="#FFFFFF"
              gradientColors={['#9C27B0', '#7b1fa2']}
            />
          </Grid>
        </Grid>

        <Divider style={{ margin: '12px 0' }} />

        {/* Gráfico de Ranking */}
        <Box>
          <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
            Ranking de Performance
          </Typography>
          <Box 
            style={{ 
              paddingBottom: separatorData.length > 8 ? '40px' : '20px',
              overflowX: 'auto',
              overflowY: 'hidden'
            }}
          >
            <Box style={{ minWidth: isMobile ? '600px' : 'auto' }}>
              <ReactApexChart
                options={chartOptions}
                series={series}
                type="bar"
                height={320}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default HourDetailModal
